import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AppLayout,
  SpaceBetween,
  Container,
  Header,
  Button,
  Box,
  Grid,
  ColumnLayout,
} from '@cloudscape-design/components';
import TopPanel from '../TopPanel';
import ServiceNavigation from '../ServiceNavigation.jsx';
import SynopsisPanelV3 from '../SynopsisPanelV3';
import ExtendingPanel from '../ExtendingPanel.jsx';
import { sendAnalytics } from '../../resources/rum-provider';
import EventLog from './EventLog';
import SellerTabs from './SellerTabs';
import BuyerWallet from './BuyerWallet';
import SessionSwitcher from './SessionSwitcher';
import SellerEnableControl from './SellerEnableControl';
import ChipRow from './ChipRow';
import Markdown from './Markdown';
import { deriveStarterChips } from './chips';
import { chatWithAgent } from './agentClient';
import { getConfig, BOT_UA } from './config';
import { newSessionId, touchSession, saveSnapshot, loadSnapshot } from './sessionStore';
import { fetchBuyerWallet, fetchSellerWallets, fetchDiscovery, mergeDiscovery } from './wallets';
import architectureImg from './images/architecture.png';
import './wafmonetization.scss';

const BUDGET_USDC = '0.10';
const demoModule = "ai-traffic-monetization";

export default function WafMonetization() {
  const cfg = useMemo(() => getConfig(), []);
  const [edge, setEdge] = useState('monetized');
  const [sessionId, setSessionId] = useState(() => newSessionId());
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]); // { role, text }
  const [events, setEvents] = useState([]); // buyer flow + sellerLog events (the Log)
  const [discovery, setDiscovery] = useState(null); // { usePortal, mode, sellers[] }
  const [suggestions, setSuggestions] = useState([]); // [{ flow, label, prompt }] per turn
  // What the agent fetched/paid for this session, keyed by seller domain (shop/
  // publisher/marquee + the 8 vertical flowIds) — drives the storefront highlights
  // and the per-vertical "Agent calls" panels.
  const [reveals, setReveals] = useState({});
  // Human-side storefront state (Emporium cart + orders) lifted here so it survives a
  // session switch (EmporiumStore remounts per session — SellerTabs is keyed on it).
  const [emporium, setEmporium] = useState({ lines: [], orders: [] });
  // Discovery allow-list: master switch + the set of sellers turned OFF for the agent
  // (empty = all enabled). Mirrors the original demo's DiscoveryControl.
  const [usePortal, setUsePortal] = useState(true);
  const [disabledSellers, setDisabledSellers] = useState([]);
  // Re-roll seed for the randomized starter chips (bump → fresh mix).
  const [chipSeed, setChipSeed] = useState(1);
  const [spent, setSpent] = useState(0);
  const [busy, setBusy] = useState(false);
  const [sellerTab, setSellerTab] = useState('shop');
  const [buyerWallet, setBuyerWallet] = useState(null);
  const [sellerWallets, setSellerWallets] = useState({});
  const abortRef = useRef(null);
  // Monotonic chat-turn counter: late `suggestions` frames (they arrive AFTER done,
  // once the input is already unlocked) must not clobber a newer turn's chips.
  const turnRef = useRef(0);
  const scrollRef = useRef(null);
  const logRef = useRef(null);
  const logStickRef = useRef(true); // follow new logs unless the user scrolled up

  // Starter chips derived from the connected sellers' discovered skills — RANDOMIZED
  // per chipSeed and filtered to the ENABLED sellers (so suggestions only offer what
  // the agent can actually do). Falls back to a generic prompt before discovery loads.
  const starterChips = useMemo(() => {
    const c = deriveStarterChips(discovery, 5, { disabledSellers, seed: chipSeed });
    return c.length ? c : [{ flow: 'recommend', label: 'Ask the agent to shop for something', prompt: 'What can you help me shop for?' }];
  }, [discovery, disabledSellers, chipSeed]);

  // Seller ids the agent should see this turn (all known minus the disabled set).
  // Omitted (undefined) when nothing is disabled → the agent uses every seller.
  const enabledSellers = useMemo(() => {
    const known = ((discovery && discovery.sellers) || []).map((s) => s.sellerId);
    return disabledSellers.length && known.length
      ? known.filter((id) => !disabledSellers.includes(id))
      : undefined;
  }, [discovery, disabledSellers]);

  const toggleSeller = (sellerId) =>
    setDisabledSellers((prev) =>
      prev.includes(sellerId) ? prev.filter((s) => s !== sellerId) : [...prev, sellerId]);

  // Usage ping on mount — the demo was opened (matches the portal-wide convention).
  useEffect(() => {
    sendAnalytics({ demo: demoModule });
  }, []);

  useEffect(() => {
    touchSession(sessionId);
  }, [sessionId]);

  // Persist the live snapshot (messages/events/spent) for the current session so it
  // can be restored on switch. Debounced via React batching; cheap for localStorage.
  useEffect(() => {
    const hasStore = emporium.lines.length || emporium.orders.length;
    if (messages.length === 0 && events.length === 0 && !hasStore) return;
    saveSnapshot(sessionId, { messages, events, spent, reveals, emporium, edge, usePortal, disabledSellers });
  }, [sessionId, messages, events, spent, reveals, emporium, edge, usePortal, disabledSellers]);

  // Load wallets (buyer balance + per-seller receiving wallets) on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      const [bw, sw, disc] = await Promise.all([
        fetchBuyerWallet(cfg),
        fetchSellerWallets(cfg.sellerBrowserBase),
        fetchDiscovery(cfg.sellerBrowserBase),
      ]);
      if (!alive) return;
      if (bw) setBuyerWallet(bw);
      if (sw.length) setSellerWallets(Object.fromEntries(sw.map((w) => [w.sellerId, w])));
      // Pre-populate Discovery so the tab isn't empty before the first chat turn;
      // the agent's own `discovered` event replaces this once it runs.
      if (disc) setDiscovery((cur) => cur || disc);
    })();
    return () => {
      alive = false;
    };
  }, [cfg]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Activity log: follow new entries as they stream in — UNLESS the user has
  // scrolled up to read history (then leave their scroll position alone).
  useEffect(() => {
    const el = logRef.current;
    if (el && logStickRef.current) el.scrollTop = el.scrollHeight;
  }, [events]);

  const onLogScroll = () => {
    const el = logRef.current;
    if (!el) return;
    // "stuck" if within 24px of the bottom.
    logStickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  // Switch to an existing session (restore its snapshot) or start a fresh one (id=null).
  const resetSession = (id) => {
    if (busy) return;
    setSuggestions([]);
    turnRef.current += 1; // orphan any in-flight turn's late chips
    fetchDiscovery(cfg.sellerBrowserBase).then((d) => setDiscovery(d));

    if (id) {
      const snap = loadSnapshot(id);
      setMessages(snap?.messages || []);
      setEvents(snap?.events || []);
      setSpent(snap?.spent || 0);
      // Restore the agent's reveals so the storefronts rebuild their cart/orders.
      setReveals(snap?.reveals || {});
      // Restore the human's storefront state (cart/orders) + the edge toggle.
      setEmporium(snap?.emporium || { lines: [], orders: [] });
      if (snap?.edge) setEdge(snap.edge);
      // Restore the discovery allow-list (master switch + disabled sellers).
      setUsePortal(snap?.usePortal !== undefined ? snap.usePortal : true);
      setDisabledSellers(snap?.disabledSellers || []);
      setChipSeed((n) => n + 1); // fresh chip mix for the restored session
      setSessionId(id);
    } else {
      setMessages([]);
      setEvents([]);
      setSpent(0);
      setReveals({});
      setEmporium({ lines: [], orders: [] });
      setUsePortal(true);
      setDisabledSellers([]);
      setChipSeed((n) => n + 1); // new session → re-roll the starter chips
      setSessionId(newSessionId());
    }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    // One usage event per real turn, tagged with the WAF posture (monetized/blocked)
    // so the data shows how the toggle is being exercised.
    sendAnalytics({ demo: demoModule, action: 'chat', posture: edge });
    setInput('');
    setBusy(true);
    const turn = ++turnRef.current;

    const history = messages.map((m) => ({ role: m.role, content: [{ text: m.text }] }));
    setMessages((prev) => [...prev, { role: 'user', text: msg }, { role: 'assistant', text: '' }]);

    const ac = new AbortController();
    abortRef.current = ac;
    let assistant = '';
    const pushEvent = (e) =>
      setEvents((prev) => [...prev, { ...e, id: e.id || `e${prev.length + 1}`, ts: e.ts || Date.now() }]);

    chatWithAgent(
      {
        invokerUrl: cfg.invokerUrl,
        message: msg,
        sellerBaseUrl: cfg.edges[edge],
        botUA: BOT_UA,
        sessionId,
        history,
        maxPayUsdc: BUDGET_USDC,
        posture: edge, // session-scoped toggle state → X-Waf-Posture per request
        usePortal, // discovery master switch
        enabledSellers, // allow-list (undefined → all sellers)
        signal: ac.signal,
      },
      {
        onAssistantDelta: (delta, meta) => {
          // reset → the agent retracted its pre-tool speculative prose; start over.
          assistant = meta?.reset ? '' : assistant + delta;
          setMessages((prev) => {
            const next = [...prev];
            for (let i = next.length - 1; i >= 0; i--) {
              if (next[i].role === 'assistant') {
                next[i] = { ...next[i], text: assistant };
                break;
              }
            }
            return next;
          });
        },
        onEvent: (e) => {
          pushEvent(e);
          // Track spend from payment events (e.g. "Paid 0.001 USDC to …").
          if (e.kind === 'payment') {
            const m = String(e.text || '').match(/([\d.]+)\s*USDC/);
            if (m) setSpent((s) => +(s + parseFloat(m[1])).toFixed(6));
            // Pay-per-request success — the monetization impact signal.
            sendAnalytics({ demo: demoModule, action: 'payment', posture: edge });
          }
        },
        onSellerLog: (e) => pushEvent({ ...e, kind: 'sellerLog' }),
        // Merge (union) the agent's per-turn discovery into the snapshot so the seller
        // tabs never shrink — the agent may report only the sellers it touched this turn.
        onDiscovered: (d) => setDiscovery((cur) => mergeDiscovery(cur, d)),
        // Late chips (post-done) are dropped if a newer turn already started.
        onSuggestions: (items) => {
          if (turnRef.current !== turn) return;
          setSuggestions(Array.isArray(items) ? items : []);
        },
        onReveal: (action, outcome, content, meta) => {
          // Key by seller: storefronts use domain (shop/publisher/marquee); verticals
          // use their sellerId/flowId. Both arrive in meta.
          const key = meta?.sellerId || meta?.domain || 'shop';
          setReveals((prev) => ({
            ...prev,
            // skillId/cardType identify the real skill (action is always "fetch").
            [key]: [...(prev[key] || []), { action, skillId: meta?.skillId, cardType: meta?.cardType, outcome, content }],
          }));
          setSellerTab(key); // surface the seller the agent just used
        },
        onDone: (outcome) => {
          setBusy(false);
          abortRef.current = null;
          // Per-turn result — `served-*` vs `lost`/`blocked`. This is the
          // monetize-vs-block contrast (there is no dedicated block event kind).
          sendAnalytics({ demo: demoModule, action: 'outcome', outcome: outcome || 'unknown', posture: edge });
          // refresh buyer balance after a turn (best-effort)
          fetchBuyerWallet(cfg).then((bw) => bw && setBuyerWallet(bw));
        },
      },
    );
  };

  const stop = () => {
    abortRef.current?.abort();
    turnRef.current += 1; // orphan the aborted turn's late chips
    setBusy(false);
  };

  return (
    <React.Fragment>
      <TopPanel label="WAF Monetization" />
      <AppLayout
        navigation={<ServiceNavigation />}
        toolsHide
        content={
          <SpaceBetween size="m">
            <SynopsisPanelV3 label="Synopsis" demotype="demo" architectureImgs={[architectureImg]}>
              An AI agent shops across seller APIs and <b>pays per request</b>, enforced natively by{' '}
              <b>AWS WAF</b>. On each seller's screen, flip how its WAF treats agents between{' '}
              <b>Monetized</b> and <b>Blocked</b> and watch the same agent either pay-and-succeed or get
              blocked and lose the content — the choice between <i>blocking bots</i> (losing the traffic
              and the revenue) and <i>monetizing</i> them.
              {!cfg.deployed && (
                <Box variant="small" color="text-status-info" margin={{ top: 'xs' }}>
                  Local mode: the agent runs on <code>:8080</code> via the dev proxy and the toggle flips
                  the local seller's posture in-process.
                </Box>
              )}
            </SynopsisPanelV3>

            {/* Buyer wallet + budget + the seller-edge toggle — one compact row */}
            <Container header={<Header variant="h2">Agent wallet &amp; budget</Header>}>
              <BuyerWallet
                wallet={buyerWallet}
                spentUsdc={spent}
                budgetUsdc={BUDGET_USDC}
                edge={edge}
                onEdgeChange={setEdge}
                edgeDisabled={busy}
              />
            </Container>

            {/* Two widgets: [chat + event log] | [seller tabs] */}
            <Grid gridDefinition={[{ colspan: { default: 12, m: 5 } }, { colspan: { default: 12, m: 7 } }]}>
              {/* LEFT — chat + event log */}
              <Container
                header={
                  <Header
                    variant="h2"
                    actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        <SellerEnableControl
                          discovery={discovery}
                          usePortal={usePortal}
                          onUsePortalChange={setUsePortal}
                          disabledSellers={disabledSellers}
                          onToggleSeller={toggleSeller}
                          onSetDisabled={setDisabledSellers}
                          disabled={busy}
                        />
                        <SessionSwitcher currentId={sessionId} onSwitch={resetSession} />
                        <Button iconName="add-plus" disabled={busy} onClick={() => resetSession(null)}>
                          New
                        </Button>
                      </SpaceBetween>
                    }
                  >
                    Agent concierge
                  </Header>
                }
              >
                <SpaceBetween size="s">
                  <div ref={scrollRef} className="wafmon-chat-scroll">
                    {messages.length === 0 ? (
                      <div>
                        <div className="wafmon-empty">
                          <div>Ask the agent — it discovers the connected sellers and pays per request.</div>
                        </div>
                        <div className="wafmon-starter-head">
                          <span>Try one of these</span>
                          <Button variant="inline-icon" iconName="refresh" ariaLabel="Shuffle suggestions"
                            onClick={() => setChipSeed((n) => n + 1)} />
                        </div>
                        {/* Starter prompts DERIVED from the connected sellers' skills,
                            randomized per chipSeed and filtered to enabled sellers. */}
                        {starterChips.map((c) => (
                          <button key={c.label} className="wafmon-starter" onClick={() => send(c.prompt)}>
                            {c.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      messages.map((m, i) => {
                        const isUser = m.role === 'user';
                        const streaming = m.role === 'assistant' && busy && i === messages.length - 1;
                        if (isUser) {
                          return (
                            <div key={i} className="wafmon-turn wafmon-turn-user">
                              <div className="wafmon-user-bubble">{m.text}</div>
                            </div>
                          );
                        }
                        return (
                          <div key={i} className="wafmon-turn">
                            <div className="wafmon-avatar wafmon-avatar-agent">🤖</div>
                            <div className={`wafmon-assistant-text ${streaming && m.text ? 'wafmon-caret' : ''}`}>
                              {m.text ? (
                                <Markdown text={m.text} />
                              ) : streaming ? (
                                <span className="wafmon-typing">
                                  <span />
                                  <span />
                                  <span />
                                </span>
                              ) : (
                                ''
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Agent's dynamic per-turn suggestions (flow-colored). Drive the
                      next turn; shown once a conversation is underway. */}
                  {messages.length > 0 && (
                    <ChipRow chips={suggestions} onPick={(p) => send(p)} disabled={busy} />
                  )}

                  {/* ChatGPT/Claude-style composer: full-width rounded bar with the
                      send/stop control docked inside on the right. */}
                  <div className="wafmon-composer">
                    {/* Never disable typing — the reader can compose their next message
                        while the agent is still finishing; only SENDING is gated on busy
                        (send() no-ops and Enter is swallowed until the turn completes). */}
                    <textarea
                      className="wafmon-composer-input"
                      value={input}
                      rows={1}
                      placeholder="Message the agent…"
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (!busy) send();
                        }
                      }}
                    />
                    {busy ? (
                      <button className="wafmon-composer-btn wafmon-composer-stop" onClick={stop} aria-label="Stop">■</button>
                    ) : (
                      <button
                        className="wafmon-composer-btn"
                        onClick={() => send()}
                        disabled={!input.trim()}
                        aria-label="Send"
                      >
                        ↑
                      </button>
                    )}
                  </div>

                  <Container header={<Header variant="h3">Agent activity</Header>}>
                    <div className="wafmon-log-scroll" ref={logRef} onScroll={onLogScroll}>
                      <EventLog events={events} />
                    </div>
                  </Container>
                </SpaceBetween>
              </Container>

              {/* RIGHT — seller storefront tabs. Keyed on sessionId so a new/reset
                  session remounts the storefronts (clearing their cart/orders). */}
              <Container header={<Header variant="h2">Sellers</Header>}>
                <SellerTabs
                  key={sessionId}
                  walletsBySeller={sellerWallets}
                  discovery={discovery}
                  reveals={reveals}
                  activeTab={sellerTab}
                  onTabChange={setSellerTab}
                  emporium={emporium}
                  onEmporiumChange={setEmporium}
                />
              </Container>
            </Grid>

            <ExtendingPanel label="Extend it">
              <ColumnLayout columns={2} variant='text-grid' className='extend_it'>
                <Box>Questions/ideas on this demo?</Box>
                <SpaceBetween direction='vertical'>
                  <Box variant='strong'>
                    Eitav Arditti, eitav@amazon.com
                  </Box>
                </SpaceBetween>
              </ColumnLayout>
            </ExtendingPanel>
          </SpaceBetween>
        }
        contentType="default"
      />
    </React.Fragment>
  );
}
